/**
 * Login Hook
 * 로그인 관련 로직 관리
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, adminLogin } from '../api/loginApi';
import { LoginRequest, LoginResponse, LoginType, SupplierLoginResponse } from '../types/login';
import { setCookie } from '../../../core/utils';
import { useAuth } from '../../../core/contexts/AuthContext';
import { loginAttemptTracker, type AttemptStatus } from '../../../core/utils/loginAttemptTracker';

export const useLogin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loginType, setLoginType] = useState<LoginType>('rider');
    const [lockStatus, setLockStatus] = useState<AttemptStatus>(() => loginAttemptTracker.getStatus());
    const navigate = useNavigate();
    const { setAuth } = useAuth();

    // 잠금 중일 때 1초마다 카운트다운 갱신
    useEffect(() => {
        if (!lockStatus.isLocked) return;
        const timer = setInterval(() => {
            const status = loginAttemptTracker.getStatus();
            setLockStatus(status);
            if (!status.isLocked) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [lockStatus.isLocked]);

    // 라이더 로그인 처리
    const handleRiderLogin = useCallback(async (userId: string, password: string) => {
        const request: LoginRequest = { userId, password };
        const response: LoginResponse = await login(request);

        if (response.success) {
            loginAttemptTracker.reset();
            setLockStatus(loginAttemptTracker.getStatus());

            // 로그인 성공 시 점검 확인 상태 초기화
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            
            localStorage.setItem('userId', userId);
            localStorage.setItem('loginType', 'rider');

            if (response.accessToken) {
                setCookie('accessToken', response.accessToken, {
                    expires: 1,
                    path: '/',
                    secure: window.location.protocol === 'https:',
                    sameSite: 'lax',
                });
            }
            if (response.refreshToken) {
                setCookie('refreshToken', response.refreshToken, {
                    expires: 1, // 24시간 (1일)
                    path: '/',
                    secure: window.location.protocol === 'https:',
                    sameSite: 'lax',
                });
            }

            const userInfo: any = {
                riderId: userId, // 라이더 ID 저장
                requiresPasswordSetup: response.requiresPasswordSetup ?? false,
                prmCode: response.prmCode, // prmCode 추가
            };

            if (response.userName) {
                userInfo.riderName = response.userName;
            }

            localStorage.setItem('userInfo', JSON.stringify(userInfo));

            // AuthContext에 인증 정보 저장 (suppType 포함)
            setAuth(
                userId,
                'RIDER',
                response.suppType || ''
            );

            if (response.requiresPasswordSetup) {
                setError(null);
                navigate('/password-setup');
                return;
            }

            // prmCode에 따라 라우팅
            if (response.prmCode === 'A') {
                // 관리자는 관리자 홈으로
                navigate('/adminHome');
            } else {
                // 일반 라이더는 홈으로
                navigate('/home');
            }
        } else {
            const newStatus = loginAttemptTracker.recordFailure();
            setLockStatus(newStatus);

            const serverMessage = response.message;
            if (serverMessage?.includes('존재하지 않는') || serverMessage?.includes('비밀번호')) {
                setError('아이디 또는 비밀번호를 확인해주세요.');
            } else {
                setError(serverMessage || '아이디 또는 비밀번호를 확인해주세요.');
            }
        }
    }, [navigate]);

    // 협력사 로그인 처리 (앱 API 사용)
    const handleSupplierLogin = useCallback(async (userId: string, password: string) => {
        const request: LoginRequest = { userId, password };
        const response: LoginResponse = await adminLogin(request); // 관리자/협력사 앱 API 사용

        if (response.success) {
            loginAttemptTracker.reset();
            setLockStatus(loginAttemptTracker.getStatus());

            // 로그인 성공 시 점검 확인 상태 초기화
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            
            localStorage.setItem('userId', userId);
            localStorage.setItem('loginType', 'supplier');

            if (response.accessToken) {
                setCookie('accessToken', response.accessToken, {
                    expires: 1,
                    path: '/',
                    secure: window.location.protocol === 'https:',
                    sameSite: 'lax',
                });
            }
            if (response.refreshToken) {
                setCookie('refreshToken', response.refreshToken, {
                    expires: 1,
                    path: '/',
                    secure: window.location.protocol === 'https:',
                    sameSite: 'lax',
                });
            }

            const userInfo: any = {
                suppId: userId, // 협력사 ID
                userName: response.userName,
                prmCode: response.prmCode, // prmCode 추가
            };

            localStorage.setItem('userInfo', JSON.stringify(userInfo));

            // AuthContext에 인증 정보 저장 (suppType 포함)
            setAuth(
                userId,
                'SUPPLIER',
                response.suppType || ''
            );

            // prmCode에 따라 라우팅
            if (response.prmCode === 'A') {
                // 관리자는 관리자 홈으로
                navigate('/adminHome');
            } else {
                // 협력사는 배달 메뉴로 (HomePageForSupplier 표시)
                navigate('/delivery?tab=summary');
            }
        } else {
            const newStatus = loginAttemptTracker.recordFailure();
            setLockStatus(newStatus);

            const serverMessage = response.message;
            if (serverMessage?.includes('존재하지 않는') || serverMessage?.includes('비밀번호')) {
                setError('아이디 또는 비밀번호를 확인해주세요.');
            } else {
                setError(serverMessage || '아이디 또는 비밀번호를 확인해주세요.');
            }
        }
    }, [navigate, setAuth]);

    // 통합 로그인 핸들러
    const handleLogin = useCallback(async (userId: string, password: string, type?: LoginType) => {
        const currentStatus = loginAttemptTracker.getStatus();
        if (currentStatus.isLocked) {
            const min = Math.floor(currentStatus.lockRemainingSeconds / 60);
            const sec = currentStatus.lockRemainingSeconds % 60;
            setError(`로그인이 잠겼습니다. ${min > 0 ? `${min}분 ` : ''}${sec}초 후 다시 시도해주세요.`);
            return;
        }

        setIsLoading(true);
        setError(null);

        const currentLoginType = type || loginType;

        try {
            if (currentLoginType === 'rider') {
                await handleRiderLogin(userId, password);
            } else {
                await handleSupplierLogin(userId, password);
            }
        } catch (err: any) {
            // 에러 메시지 처리: 서버 응답 메시지 우선 사용
            const errorMessage = err.message || err.response?.data?.message;

            if (errorMessage) {
                setError(errorMessage);
            } else {
                // 기본 에러 메시지: 아이디/비밀번호 확인 안내
                setError('아이디 또는 비밀번호를 확인해주세요.');
            }
        } finally {
            setIsLoading(false);
        }
    }, [loginType, handleRiderLogin, handleSupplierLogin]);

    return {
        handleLogin,
        isLoading,
        error,
        loginType,
        setLoginType,
        lockStatus,
    };
};

